import { useMemo } from 'react'
import { useRouter } from 'next/router'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'

import { assetUrl } from 'utils/url'
import {
  useAPIContentKinerjaUtama,
  useAPIKinerja,
} from 'api/kinerja/useAPIKinerja'
import { IndikatorPerencanaanPanel } from 'app/KinerjaShared/components/IndikatorPerencanaanPanel'
import { isSelectableTugasPokok } from 'app/KinerjaGuruPage/utils/perencanaan'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useUpsertPerencanaanPegawai } from 'app/KinerjaShared/hooks/useUpsertPerencanaanPegawai'

import type { KinerjaUtamaDetail, UpsertSKPPayload } from 'types/kinerja'

function kinerjaUtamaPanelTitle(item: KinerjaUtamaDetail): string {
  if (isSelectableTugasPokok(item.tag)) {
    return item.targetKualitas
  }
  return item.rencanaHasilKerja
}

type TugasPokokProps = {
  onNext: () => void
}

export function TugasPokok({ onNext }: TugasPokokProps) {
  const router = useRouter()

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour,
      },
      {
        tipe: 'SPNF',
      }
    )

  // -----------------------------------------
  // Get Kinerja Utama content
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'SPNF',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  const kinerjaUtamaItems = useMemo(
    () => dataContentKinerjaUtama?.data?.kinerjaUtama ?? [],
    [dataContentKinerjaUtama]
  )

  // -----------------------------------------
  // Check if needs initial submission
  // -----------------------------------------
  const needsInitialSubmission = useMemo(() => {
    if (!kinerjaUtamaItems.length || isPerencanaanLoading) {
      return false
    }

    // Return true if there is no saved Kinerja Utama data
    const saved = perencanaanData?.data?.kinerjaUtama
    return !saved || saved.length === 0
  }, [
    isPerencanaanLoading,
    kinerjaUtamaItems.length,
    perencanaanData?.data?.kinerjaUtama,
  ])

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaanPegawai()

  const payload: UpsertSKPPayload = useMemo(
    () => ({
      id: perencanaanData?.data?.id,
      kinerjaUtama: kinerjaUtamaItems.map((item) => item.id),
      tugasTambahan: [],
      tipe: 'SPNF',
    }),
    [kinerjaUtamaItems, perencanaanData?.data?.id]
  )

  const handleSaveToDraft = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !needsInitialSubmission, // skip mutation if not needs initial submission
      showSuccessMessage: true,
      onSuccess: () => router.push(PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH),
    })

  const handleNext = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !needsInitialSubmission, // skip mutation if not needs initial submission
      onSuccess: onNext,
    })

  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingQueries = isPerencanaanLoading || isLoadingContentKinerjaUtama
  const isLoading = isLoadingQueries || isLoadingUpsertPerencanaan

  return (
    <>
      <div className="p-9">
        <div className="flex items-center gap-3 mb-6">
          <img
            src={assetUrl('/illustrations/img-badge-star-ribbon-pro.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={64}
            height={64}
          />
          <div className="space-y-1">
            <Text as="h2" variant="heading-md">
              Pelaksanaan Tugas Pokok
            </Text>
            <Text color="subdued">
              Berikut tugas keseharian Anda sebagai Pamong Belajar di satuan
              pendidikan. Anda tidak perlu unggah Bukti Dukung.
            </Text>
          </div>
        </div>

        {isLoadingQueries ? (
          <div className="space-y-6">
            <Shimmer width="100%" height="124px" noOfLines={7} />
          </div>
        ) : (
          <div className="space-y-6">
            {kinerjaUtamaItems.map((item, index) => (
              <IndikatorPerencanaanPanel
                key={item.id}
                orderNumber={index + 1}
                title={kinerjaUtamaPanelTitle(item)}
              />
            ))}
          </div>
        )}
      </div>
      <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
        <Button
          size="lg"
          color="white"
          disabled={isLoading}
          onClick={handleSaveToDraft}
        >
          Simpan draf
        </Button>

        <Button
          color="black"
          size="lg"
          disabled={isLoading}
          onClick={handleNext}
        >
          Ke Praktik Kinerja
        </Button>
      </div>
    </>
  )
}
